import { useState } from 'react';

interface Categorie {
  id: string;
  nom: string;
  couleur: string;
}

interface CategoriesManagerModalProps {
  categories: Categorie[];
  onClose: () => void;
  onChange: (cats: Categorie[]) => void;
}

const COULEURS = [
  { value: 'bg-foret-100 text-foret-700', label: 'Vert' },
  { value: 'bg-terre-100 text-terre-700', label: 'Terre' },
  { value: 'bg-or-100 text-or-700', label: 'Or' },
  { value: 'bg-pink-100 text-pink-700', label: 'Rose' },
  { value: 'bg-teal-100 text-teal-700', label: 'Teal' },
  { value: 'bg-orange-100 text-orange-700', label: 'Orange' },
];

export default function CategoriesManagerModal({ categories, onClose, onChange }: CategoriesManagerModalProps) {
  const [cats, setCats] = useState<Categorie[]>(categories.map(c => ({ ...c })));
  const [newNom, setNewNom] = useState('');
  const [newCouleur, setNewCouleur] = useState(COULEURS[0].value);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editNom, setEditNom] = useState('');
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const handleAdd = () => {
    const trimmed = newNom.trim();
    if (!trimmed || cats.find(c => c.nom.toLowerCase() === trimmed.toLowerCase())) return;
    const id = `cat_${Date.now()}`;
    setCats(prev => [...prev, { id, nom: trimmed, couleur: newCouleur }]);
    setNewNom('');
    setNewCouleur(COULEURS[0].value);
  };

  const startEdit = (cat: Categorie) => {
    setEditingId(cat.id);
    setEditNom(cat.nom);
  };

  const handleSaveEdit = (id: string) => {
    const trimmed = editNom.trim();
    if (!trimmed) return;
    setCats(prev => prev.map(c => c.id === id ? { ...c, nom: trimmed } : c));
    setEditingId(null);
  };

  const handleDelete = (id: string) => {
    setCats(prev => prev.filter(c => c.id !== id));
    setDeleteId(null);
  };

  const handleConfirm = () => {
    onChange(cats);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-lg" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <h3 className="font-playfair font-bold text-lg text-gray-900">Gérer les catégories</h3>
            <p className="text-xs text-gray-400 mt-0.5">{cats.length} catégories</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-xl bg-gray-100 hover:bg-gray-200 cursor-pointer text-gray-500">
            <i className="ri-close-line" />
          </button>
        </div>

        <div className="px-6 py-4 max-h-[60vh] overflow-y-auto">
          {/* Add new */}
          <div className="bg-foret-50 rounded-xl p-4 mb-5">
            <p className="text-xs font-semibold text-foret-800 mb-3">Ajouter une catégorie</p>
            <div className="flex gap-2 mb-3">
              <input
                type="text"
                value={newNom}
                onChange={e => setNewNom(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleAdd()}
                placeholder="Nom de la catégorie…"
                className="flex-1 px-3 py-2 text-sm border border-foret-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-foret-400 bg-white"
              />
              <button
                onClick={handleAdd}
                disabled={!newNom.trim()}
                className="px-4 py-2 bg-foret-500 text-white text-sm font-semibold rounded-xl cursor-pointer hover:bg-foret-600 disabled:opacity-50 whitespace-nowrap"
              >
                Ajouter
              </button>
            </div>
            <div className="flex gap-2 flex-wrap">
              {COULEURS.map(c => (
                <button
                  key={c.value}
                  onClick={() => setNewCouleur(c.value)}
                  className={`text-xs px-3 py-1 rounded-full font-medium cursor-pointer border-2 transition-all ${c.value} ${newCouleur === c.value ? 'border-gray-500 scale-105' : 'border-transparent'}`}
                >
                  {c.label}
                </button>
              ))}
            </div>
          </div>

          {/* List */}
          <div className="space-y-2">
            {cats.map(cat => (
              <div key={cat.id} className="flex items-center gap-3 bg-gray-50 rounded-xl px-4 py-2.5 group">
                {editingId === cat.id ? (
                  <>
                    <input
                      autoFocus
                      type="text"
                      value={editNom}
                      onChange={e => setEditNom(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') handleSaveEdit(cat.id); if (e.key === 'Escape') setEditingId(null); }}
                      className="flex-1 px-2 py-1 text-sm border border-foret-300 rounded-lg focus:outline-none bg-white"
                    />
                    <button onClick={() => handleSaveEdit(cat.id)} className="text-xs px-3 py-1 bg-foret-500 text-white rounded-lg cursor-pointer font-semibold whitespace-nowrap">OK</button>
                    <button onClick={() => setEditingId(null)} className="text-xs px-2 py-1 bg-gray-200 text-gray-600 rounded-lg cursor-pointer whitespace-nowrap">✕</button>
                  </>
                ) : (
                  <>
                    <span className={`text-xs px-2.5 py-0.5 rounded-full font-medium ${cat.couleur}`}>{cat.nom}</span>
                    <div className="flex-1" />
                    {deleteId === cat.id ? (
                      <>
                        <span className="text-xs text-gray-500">Supprimer ?</span>
                        <button onClick={() => handleDelete(cat.id)} className="text-xs px-2 py-0.5 bg-red-500 text-white rounded-lg cursor-pointer whitespace-nowrap">Oui</button>
                        <button onClick={() => setDeleteId(null)} className="text-xs px-2 py-0.5 bg-gray-200 rounded-lg cursor-pointer whitespace-nowrap">Non</button>
                      </>
                    ) : (
                      <div className="flex gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button onClick={() => startEdit(cat)} className="w-6 h-6 flex items-center justify-center rounded-lg bg-white border border-gray-200 hover:border-foret-300 hover:text-foret-600 text-gray-400 cursor-pointer transition-colors">
                          <i className="ri-pencil-line text-xs" />
                        </button>
                        <button onClick={() => setDeleteId(cat.id)} className="w-6 h-6 flex items-center justify-center rounded-lg bg-white border border-gray-200 hover:border-red-300 hover:text-red-500 text-gray-400 cursor-pointer transition-colors">
                          <i className="ri-delete-bin-line text-xs" />
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="flex gap-3 px-6 py-4 border-t border-gray-100">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-xl border border-gray-200 text-gray-600 text-sm font-semibold cursor-pointer hover:bg-gray-50 whitespace-nowrap">Annuler</button>
          <button onClick={handleConfirm} className="flex-1 py-2.5 rounded-xl bg-foret-500 text-white text-sm font-semibold cursor-pointer hover:bg-foret-600 whitespace-nowrap">Confirmer les modifications</button>
        </div>
      </div>
    </div>
  );
}
